import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PlantingSiteDto } from '../../../../models/trofaco/plantingSiteDto';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { FilterListComponent } from './filter-list.component';
import { BaseComponent } from '../bases/base-component';
import { CertificateDto } from 'src/app/models/trofaco/certificateDto';
import { AppUserModel } from 'src/app/models/users/app-user.model';
import { UserService } from '../../services/user.service';

@Component({
    selector: 'app-planting-sites-shell',
    templateUrl: './planting-sites-shell.component.html',
    styleUrls: ['./planting-sites-shell.component.css']
})
export class PlantingSitesShellComponent extends BaseComponent implements OnInit {
    @ViewChild(FilterListComponent, {static: true}) filterComponent: FilterListComponent;
    searchText: string | null;
    sortText: string | null;
    sortType: string | null;
    totalPlantedTrees: number;
    sortDirections = [{
        value: 'new',
        description: 'Newest to oldest'
    },{
        value: 'old',
        description: 'Oldest to newest'
    }];
    currentUser: AppUserModel;
    getSortDescription(isNewest: string) {
        const sort = this.sortDirections.find((item) => item.value === isNewest);
        return sort.description;
    }

    @Input() backendCall: boolean = false;

    @Input() unfilteredSites: PlantingSiteDto[];

    @Output()
    plantingSitesChange = new EventEmitter<PlantingSiteDto[]>(true);

    @Output()
    onUpdatePageSize = new EventEmitter<number>();

    @Input()
    showAddNew = false;

    @Input()
    pageSize: number;

    @Input() unfilteredSitesChanged$: Observable<PlantingSiteDto[]> = new Observable<PlantingSiteDto[]>();
    sortMissingType:string;
    sortByMissingDirections = [{
        value: 'all',
        description: 'Show all sites'
    },{
        value: 'complete',
        description: 'Complete sites'
    },
    {
        value: 'incomplete',
        description: 'Incomplete sites'
    }];

    constructor(protected router: Router, 
        protected route: ActivatedRoute,
        private userService: UserService) {
        super();
    }

    ngOnInit() {
        if(this.backendCall) this.getCurrentUser();
        const getSortType : string = this.route.snapshot.queryParamMap.get('sortType');
        this.sortMissingType = getSortType || "all";

        let search$ = this.route.queryParamMap
            .pipe(
                map(map => map.get('search')),
                distinctUntilChanged(),
                tap(searchText => this.searchText = searchText)
            );
        let sortText$ = this.route.queryParamMap
            .pipe(
                map(map => map.get('sort')),
                distinctUntilChanged(),
                tap(sortText => this.sortText = sortText ? sortText : 'new')
            );

        let sortType$ = this.route.queryParamMap
            .pipe(
                map(map => map.get('sortType')),
                distinctUntilChanged(),
                tap(sortType => this.sortType = sortType)
            );    

        let filter$ = this.filterComponent.filterChange.asObservable();

        combineLatest([sortText$, search$,sortType$, filter$])
            .pipe(
                debounceTime(0),
                map(([sortText, searchText,sortType, filter]) => {
                    return this.unfilteredSites
                        .filter(value => filter(value) && this.quickSearch(value, searchText))
                        .filter(value => filter(value) && this.filterMissing(value, sortType))
                        .sort((siteA, siteB) => this.sortSites(siteA, siteB, sortText));
                })
            )
            .subscribe(this.plantingSitesChange);

        this.totalPlantedTrees = this.unfilteredSites.reduce((prev: number, next: PlantingSiteDto) => {
            return prev + next.totalTrees;
        }, 0);

        this.unfilteredSitesChanged$.subscribe((sites: PlantingSiteDto[]) => {
            this.unfilteredSites = sites;
            this.filterComponent.filterUpdated();
        });
    }

    searchChanged() {
        let {search, ...params} = this.route.snapshot.queryParams;
        if (this.searchText) {
            params.search = this.searchText;
        }
        this.router.navigate([], {
            relativeTo: this.route,
            replaceUrl: true,
            queryParams: params,
        });
    }

    quickSearch(site: PlantingSiteDto, searchText: string | null): boolean {
        if (!searchText) return true;
        let lowerSearchText = searchText.toLowerCase();
        if (site.title && site.title.toLowerCase().includes(lowerSearchText)) return true;
        if (site.code && site.code.toLowerCase().includes(lowerSearchText)) return true;
        return false;
    }

    updatePageSize(){
        this.onUpdatePageSize.emit(this.pageSize);
    }

    sortSites(siteA: PlantingSiteDto, siteB: PlantingSiteDto, sortText: string): number {
        const sortNew = !sortText || sortText === 'new';
        const plantingDateTimeA = siteA.plantingDate instanceof Date ? siteA.plantingDate : new Date(siteA.plantingDate);
        const plantingDateTimeB = siteB.plantingDate instanceof Date ? siteB.plantingDate : new Date(siteB.plantingDate);

        const plantingDateA = plantingDateTimeA.toDateString();
        const plantingDateB = plantingDateTimeB.toDateString();

        if (plantingDateA === plantingDateB) {
            const timeA = { hour: plantingDateTimeA.getHours(), minute: plantingDateTimeA.getMinutes(), second: plantingDateTimeA.getSeconds() };
            const timeB = { hour: plantingDateTimeB.getHours(), minute: plantingDateTimeB.getMinutes(), second: plantingDateTimeB.getSeconds() };

            if (timeA.hour !== timeB.hour) return this.compareSites(timeA.hour, timeB.hour, sortNew);
            if (timeA.minute !== timeB.minute) return this.compareSites(timeA.minute, timeB.minute, sortNew);
            if (timeA.second !== timeB.second) return this.compareSites(timeA.second, timeB.second, sortNew);

            return 0;
        }

        if (plantingDateTimeA.getTime() > plantingDateTimeB.getTime()) return sortNew ? -1 : 1;
        if (plantingDateTimeA.getTime() < plantingDateTimeB.getTime()) return sortNew ? 1 : -1;

        return 0;
    }

    navigateSortSites(sortType: string) {
        let { sort, ...params } = this.route.snapshot.queryParams;
        params.sort = sortType;

        this.router.navigate([], {
            relativeTo: this.route,
            replaceUrl: true,
            queryParams: params
        });
    }

    changeSortMissingSites() {
        let { sortType, ...params } = this.route.snapshot.queryParams;
        params.sortType = this.sortMissingType;

        this.router.navigate([], {
            relativeTo: this.route,
            replaceUrl: true,
            queryParams: params
        });
    }

    compareSites(statementA, statementB, sortNew) {
        if (statementA < statementB) return sortNew ? -1 : 1;
        else if (statementA > statementB) return sortNew ? 1 : -1;
        else return 0;
    }

    async getCurrentUser() {
        this.currentUser = await this.userService.getCurrentUserAsync();
    }

    filterMissing(site: PlantingSiteDto, sortType: string | null): boolean {      
        if (!sortType) return true;
        if (sortType == "all") return true
        if (sortType == "complete" && this.isEmptyObject(site.missingData)) return true
        if (sortType == "incomplete" && !this.isEmptyObject(site.missingData)) return true
        return false;
    }

    isEmptyObject(obj) {
        return (obj && (Object.keys(obj).length === 0));
      }

}
